import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Text from 'components/inputs/text';
import Textarea from 'components/inputs/textarea';
import { TextKeyType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { errorModal, successModal } from 'utils/ui';

export default function TextKeysPage() {
  const dispatch = useAppDispatch();
  const { textKeys } = useAppSelector((state) => state.admin);
  const [localKeys, setLocalKeys] = useState<TextKeyType[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLocalKeys(textKeys);
  }, [textKeys]);

  const filteredKeys = useMemo(() => {
    if (!search) return localKeys;
    const lower = search.toLowerCase();
    return localKeys.filter(
      (tk) => tk.code.includes(lower) || tk.value.toLowerCase().includes(lower)
    );
  }, [localKeys, search]);

  const updateValue = (code: string, value: string) => {
    setLocalKeys((prev) =>
      prev.map((tk) => (tk.code === code ? { ...tk, value } : tk))
    );
  };

  const hasChanges = JSON.stringify(localKeys) !== JSON.stringify(textKeys);

  const handleSave = async () => {
    try {
      await axios.patch('text-keys', localKeys);
      dispatch(fetchDefaultAdminData());
      successModal('Text keys updated successfully.');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    setLocalKeys(textKeys);
  };

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>Text Keys</div>
      <div className='flex items-center gap-3'>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={!hasChanges}
        />
        <Button
          text='Reset'
          color='red'
          onClick={handleReset}
          disabled={!hasChanges}
        />
        <div className='ml-auto w-64'>
          <Text
            value={search}
            onChange={setSearch}
            placeholder='Search keys or values...'
            controlled
          />
        </div>
      </div>
      <div className='flex flex-col gap-3'>
        {filteredKeys.map((tk) => (
          <div
            key={tk.code}
            className='flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'
          >
            <div className='w-72 shrink-0 pt-2'>
              <span className='rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-700'>
                {tk.code}
              </span>
            </div>
            <div className='min-w-0 flex-1'>
              <Textarea
                value={tk.value}
                onChange={(value) => updateValue(tk.code, value)}
                rows={2}
                controlled
              />
            </div>
          </div>
        ))}
        {filteredKeys.length === 0 && (
          <div className='py-8 text-center text-gray-500'>
            No text keys match your search.
          </div>
        )}
      </div>
    </div>
  );
}
